import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';

const ApplyTemplateDialog = ({ open, onOpenChange, template, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState('');

  useEffect(() => {
    if (open) {
      fetchLists();
    }
  }, [open]);

  const fetchLists = async () => {
    try {
      const response = await API.get('/shopping/lists?status=active');
      setLists(response.data);
      if (response.data.length > 0) {
        setSelectedList(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch lists:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedList) {
      toast.error('Please select a list');
      return;
    }
    
    setLoading(true);
    try {
      await API.post(`/shopping/templates/${template.id}/apply?list_id=${selectedList}`);
      toast.success('Template applied!');
      onSuccess();
    } catch (error) {
      console.error('Failed to apply template:', error);
      toast.error('Failed to apply template');
    } finally {
      setLoading(false);
    }
  };

  if (!template) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Apply Template</DialogTitle>
          <DialogDescription>
            Add {template.items?.length || 0} items from "{template.name}" to a list
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Select List</Label>
            <Select value={selectedList} onValueChange={setSelectedList}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a list..." />
              </SelectTrigger>
              <SelectContent>
                {lists.map(list => (
                  <SelectItem key={list.id} value={list.id}>
                    {list.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {template.items && template.items.length > 0 && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-2">Items to add:</p>
              <div className="flex flex-wrap gap-1">
                {template.items.map((item, idx) => (
                  <span key={idx} className="text-xs bg-white px-2 py-1 rounded border">
                    {item.name}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {lists.length === 0 && (
            <p className="text-sm text-gray-500">
              No active lists. Create a list first.
            </p>
          )}
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading || !selectedList}
              className="bg-[#D4E5C7] hover:bg-[#C4D5B7] text-gray-900"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Apply Template
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ApplyTemplateDialog;
